import {
  differenceInDays,
  format,
  isSameDay,
  subDays,
  subMonths,
} from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import * as React from "react";
import { DateRange, Matcher, OnSelectHandler } from "react-day-picker";

import { Button } from "../ui/button";
import { Calendar } from "../ui/calendar";
import { Side } from "../ui/CustomChervonIcon";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

import YearSliderFilter from "./YearSliderFilter";

import { ZERO } from "@/app/constants/constants";
import { Checkbox } from "@/components/ui/checkbox";
import { useMediaQuery } from "@/hooks/browser/useMediaQuery";
import { cn } from "@/utils/core/generalUtils";
import { LLL_DD_YEAR_FORMAT } from "@/utils/datetime/dateTimeHelper";

enum DateRangePreset {
  LAST_7_DAYS = "Last 7 Days",
  LAST_15_DAYS = "Last 15 Days",
  LAST_1_MONTH = "Last 1 Month",
  LAST_6_MONTH = "Last 6 Months",
}

export type DateRangePresetsMultiSelectionConfig = {
  options: {
    key: string;
    value: string;
  }[];
  selectedOptions: string[];
  setSelectedOptions: (val: string[]) => void;
};

// Define the props type to include onChange
type DateRangePickerWithPresetsProps = React.HTMLAttributes<HTMLDivElement> & {
  value: DateRange | undefined;
  onChange?: (date: DateRange | undefined) => void;
  onApply?: (date: DateRange | undefined, selectedOptions: string[]) => void;
  side?: Side;
  customIcon?: React.ReactNode;
  matcher?: Matcher[];
  multiSelection?: DateRangePresetsMultiSelectionConfig;
  showYearSliderFilter?: boolean;
  showSelectedDate?: boolean;
  disableFutureDates?: boolean;
};

const formatDateRange = (date: DateRange | undefined): string | null => {
  if (!date?.from || !date?.to) {
    return null;
  }

  // If it's the same day, show only one date
  if (isSameDay(date.to, date.from)) {
    return format(date.from, LLL_DD_YEAR_FORMAT);
  }

  return `${format(date.from, LLL_DD_YEAR_FORMAT)} - ${format(date.to, LLL_DD_YEAR_FORMAT)}`;
};

const handleDisableFutureDates = (date: Date): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return date > today;
};

const getPresetFromDateRange = (
  dateRange: DateRange | undefined,
): DateRangePreset | undefined => {
  if (!dateRange?.from || !dateRange?.to) {
    return undefined;
  }

  const now = new Date();

  const sameDay = isSameDay(dateRange.to, dateRange.from);
  const isToday = isSameDay(dateRange.to, now);

  if (sameDay || !isToday) {
    return undefined;
  }

  const { from } = dateRange;
  const daysDiff = differenceInDays(now, from);

  // Use date-fns for reliable comparisons
  if (daysDiff === 6 && isSameDay(from, subDays(now, 6))) {
    return DateRangePreset.LAST_7_DAYS;
  }

  if (daysDiff === 14 && isSameDay(from, subDays(now, 14))) {
    return DateRangePreset.LAST_15_DAYS;
  }

  const oneMonthAgo = subMonths(now, 1);
  if (isSameDay(from, oneMonthAgo) && daysDiff <= 31) {
    return DateRangePreset.LAST_1_MONTH;
  }

  const sixMonthsAgo = subMonths(now, 6);
  if (isSameDay(from, sixMonthsAgo)) {
    return DateRangePreset.LAST_6_MONTH;
  }

  return undefined;
};

const DateRangePickerWithPresets: React.FC<DateRangePickerWithPresetsProps> = ({
  className,
  value,
  onChange,
  onApply,
  side,
  customIcon,
  matcher,
  multiSelection,
  showYearSliderFilter = true,
  disableFutureDates = false,
}) => {
  const [isOpen, setOpen] = React.useState<boolean>(false);

  const isMediumScreen = useMediaQuery("(max-width: 1407px)");

  const [selectedItems, setSelectedItems] = React.useState<string[]>(
    multiSelection?.selectedOptions ?? [],
  );

  const [date, setDate] = React.useState<DateRange | undefined>(value);

  const [month, setMonth] = React.useState<Date | undefined>(date?.from);

  const selectedPreset = React.useMemo(
    () => getPresetFromDateRange(date),
    [date],
  );

  const hasAppliedDate = React.useMemo(
    () => Boolean(value?.from && value?.to),
    [value],
  );

  const getIsChecked = React.useCallback(
    (val: string): boolean => selectedItems.includes(val),
    [selectedItems],
  );

  const handleSetDate = (): void => {
    if (onApply) {
      onApply(date, selectedItems);
    } else {
      multiSelection?.setSelectedOptions(selectedItems);
      onChange?.(date);
    }
    setOpen(false);
  };

  const handleClearDates = React.useCallback((): void => {
    setDate({ from: undefined, to: undefined });
    setMonth(undefined);
  }, []);

  const handleMultiSelectionChange = (
    checked: boolean,
    value: string,
  ): void => {
    if (checked) {
      setSelectedItems((prev) => [...prev, value]);
    } else {
      setSelectedItems((prev) => prev.filter((item) => item !== value));
    }
  };

  const handlePresetSelect = React.useCallback(
    (preset: DateRangePreset): void => {
      if (selectedPreset === preset) {
        handleClearDates();

        return;
      }

      const now = new Date();
      let from: Date;

      switch (preset) {
        case DateRangePreset.LAST_7_DAYS:
          from = subDays(now, 6);
          break;
        case DateRangePreset.LAST_15_DAYS:
          from = subDays(now, 14);
          break;
        case DateRangePreset.LAST_1_MONTH:
          from = subMonths(now, 1);
          break;
        case DateRangePreset.LAST_6_MONTH:
          from = subMonths(now, 6);
          break;
        default:
          return;
      }

      setDate({ from, to: now });
      setMonth(from);
    },
    [selectedPreset, setDate, setMonth, handleClearDates],
  );

  const handleYearChange = (date: DateRange): void => {
    if (date.from && date.to) {
      setDate(date);
      setMonth(new Date(date.from.getFullYear(), 0, 1));
    } else {
      handleClearDates();
    }
  };

  const handleCalendarSelect: OnSelectHandler<DateRange | undefined> = (
    range,
  ): void => {
    // If only 'from' is selected (single day click), set 'to' to the same date
    if (range?.from && !range?.to) {
      setDate({ from: range.from, to: range.from });
    } else {
      setDate(range);
    }
  };

  const handleResetCalendar = React.useCallback((): void => {
    handleClearDates();
    setSelectedItems([]);
  }, [handleClearDates]);

  const onClearClick = React.useCallback((): void => {
    handleResetCalendar();
    if (onApply) {
      onApply(undefined, []);
    } else {
      multiSelection?.setSelectedOptions([]);
      onChange?.(undefined);
    }
  }, [handleResetCalendar, onApply, onChange, multiSelection]);

  const formattedAppliedDateRange = React.useMemo(
    () => formatDateRange(value),
    [value],
  );
  const formattedDateRange = React.useMemo(() => formatDateRange(date), [date]);

  React.useEffect((): void => {
    if (!value) {
      handleResetCalendar();

      return;
    }

    setDate(value);
    setMonth(value.from ?? undefined);
  }, [value, handleResetCalendar]);

  return (
    <div className={cn("grid gap-2 w-fit ", className)}>
      <Popover open={isOpen} onOpenChange={() => setOpen(!isOpen)}>
        <PopoverTrigger asChild>
          <Button
            id='date'
            variant={"outline"}
            className={cn(
              "w-[300px] justify-start text-left font-normal",
              !date && "text-muted-foreground",
              hasAppliedDate && "text-primary bg-primaryLight",
              className,
            )}
          >
            <CalendarIcon
              className={`w-4 h-4 opacity-30 ${hasAppliedDate ? "mr-2" : ""}`}
            />
            {hasAppliedDate && <>{formattedAppliedDateRange}</>}
            {customIcon}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className='w-auto p-0 m-auto mr-2'
          align='start'
          role='dialog'
          aria-label='Select date range'
          side={side}
        >
          <div
            className={cn("flex flex-row justify-center", {
              "flex-col": isMediumScreen,
            })}
          >
            <div
              id='presets-container'
              role='tablist'
              aria-label='Date presets'
              className={cn("divide-y px-4", {
                "order-2 border-t": isMediumScreen,
              })}
            >
              {multiSelection && multiSelection.options && (
                <div className='pt-4 pb-2 min-w-64 space-y-1'>
                  {multiSelection.options.map((opt) => (
                    <label
                      key={opt.value}
                      htmlFor={opt.value}
                      className='flex items-center space-x-3 hover:bg-primaryLight p-2 rounded-md cursor-pointer'
                    >
                      <Checkbox
                        id={opt.value}
                        checked={getIsChecked(opt.value)}
                        onCheckedChange={(checked) => {
                          handleMultiSelectionChange(!!checked, opt.value);
                        }}
                      />
                      <span className='font-normal text-sm'>{opt.key}</span>
                    </label>
                  ))}
                </div>
              )}

              <div className='py-2 flex flex-col items-start w-full space-y-1'>
                {Object.values(DateRangePreset).map((preset) => (
                  <button
                    key={preset}
                    role='tab'
                    aria-selected={selectedPreset === preset}
                    className={cn(
                      "flex text-sm items-center space-x-3 hover:bg-primaryLight p-2.5 rounded-md cursor-pointer w-full",
                      selectedPreset === preset && "bg-primaryLight",
                    )}
                    onClick={() => handlePresetSelect(preset)}
                  >
                    {preset}
                  </button>
                ))}
              </div>
            </div>

            <div className='border-l'>
              <div className='p-4 flex space-x-3'>
                <div
                  className={
                    "w-full flex items-center border p-2 rounded-sm space-x-4"
                  }
                >
                  <CalendarIcon className={"w-4 h-4 opacity-30"} />
                  <span className='text-sm opacity-80'>
                    {formattedDateRange ?? "Select date range"}
                  </span>
                </div>

                {showYearSliderFilter && (
                  <YearSliderFilter
                    className='rounded-sm'
                    selectedDate={date}
                    onYearChange={handleYearChange}
                    syncWithQuery={false}
                    {...(disableFutureDates && {
                      forwardYearOffset: ZERO,
                    })}
                  />
                )}
              </div>

              <div className='relative'>
                <div className='pt-2'>
                  <Calendar
                    month={month}
                    onMonthChange={setMonth}
                    mode='range'
                    selected={date}
                    onSelect={handleCalendarSelect}
                    disabled={
                      disableFutureDates ? handleDisableFutureDates : matcher
                    }
                    numberOfMonths={2}
                  />
                </div>
              </div>
              <div className='flex flex-row-reverse gap-2 mb-2 mr-4'>
                <Button
                  disabled={!(date?.to && date.from)}
                  onClick={handleSetDate}
                >
                  Apply
                </Button>
                <Button
                  disabled={!hasAppliedDate}
                  variant={"ghost"}
                  onClick={onClearClick}
                >
                  Clear
                </Button>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default DateRangePickerWithPresets;
